import { LoadingOutlined } from '@ant-design/icons';
import { t } from '@hefe/i18n';
import {
  KnowledgeBaseCategory,
  RequestBasePostPutKnowledgeBaseArticle,
  SelectOption,
} from '@hefe/models';
import { KnowledgeBaseApi } from '@hefe/store';
import { useHtmlWithAuthImages } from '@hefe/utils';
import { Breadcrumb, Button, Col, Result, Row } from 'antd';
import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import CustomForm from '../../../core/components/form/custom-form';
import KnowledgeBaseLayout from '../../layout/knowledge-base.layout';

export default function AddEditKnowledgeBaseArticlePage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation();

  const articleId = parseInt(id ?? 'a', 10);

  const { data: navigation = [], isFetching: isFetchingNavigation } =
    KnowledgeBaseApi.useFetchNavigationQuery();

  const [createArticle, { isLoading: isCreatingArticle }] =
    KnowledgeBaseApi.useCreateArticleMutation();

  const [editArticle, { isLoading: isEditingArticle }] =
    KnowledgeBaseApi.useEditArticleMutation();

  const { data: article, isFetching: isFetchingArticle } =
    KnowledgeBaseApi.useFetchArticleQuery(articleId, {
      skip: isNaN(articleId),
    });

  const { data: articleText, loading: isLoadingText } = useHtmlWithAuthImages(
    article?.content ?? ''
  );

  const saveArticle = async (values: any) => {
    const request: RequestBasePostPutKnowledgeBaseArticle = {
      title: values.title,
      content: values.content,
      category_id: values.category_id ?? null,
    };

    if (article?.id) {
      editArticle({
        id: article?.id ?? 0,
        request: request,
      })
        .unwrap()
        .then((updatedArticle) => {
          navigate(`/knowledge-base/articles/${updatedArticle?.id}`);
        })
        .catch((_) => undefined);
    } else {
      createArticle(request)
        .unwrap()
        .then((newArticle) => {
          navigate(`/knowledge-base/articles/${newArticle?.id}`);
        })
        .catch((error) => undefined);
    }
  };

  const breadcrumbs = useMemo(() => {
    return (
      article?.breadcrumbs?.map((breadcrumb) => ({
        key: breadcrumb.id,
        title: breadcrumb.name,
        onClick: () => navigate(`/knowledge-base/categories/${breadcrumb.id}`),
      })) ?? []
    );
  }, [article, navigate]);

  const categoryTreeData = useMemo(() => {
    const navigationNode = (category: KnowledgeBaseCategory): SelectOption => {
      return {
        label: category.name,
        value: category.id,
        children: category?.children?.map((child) => navigationNode(child)),
      } as SelectOption;
    };

    const returnData = navigation.map((category) => navigationNode(category));

    return [
      ...returnData,
      {
        label: t('knowledge_base.categories.uncategorized'),
        value: 0,
      },
    ];
  }, [navigation]);

  const navigateBack = () => {
    navigate('/knowledge-base');
  };

  const renderContent = () => {
    if (id === undefined) {
      return renderForm();
    } else if (isFetchingArticle || isLoadingText) {
      return renderLoading();
    } else if (!article) {
      return renderNoContent();
    } else {
      return renderForm();
    }
  };

  const renderNoContent = () => {
    return (
      <Result
        status="404"
        title={t('knowledge_base.articles.detail_page.not_found')}
        extra={
          <Button type="primary" onClick={navigateBack}>
            {t('back')}
          </Button>
        }
      />
    );
  };

  const renderLoading = () => {
    return (
      <Result
        icon={<LoadingOutlined />}
        title={t('knowledge_base.articles.detail_page.loading')}
      />
    );
  };

  const renderForm = () => {
    return (
      <Row>
        <Col xs={24}>
          {breadcrumbs?.length > 0 && (
            <Breadcrumb
              style={{ cursor: 'pointer', marginBottom: 16 }}
              items={[
                {
                  title: t('knowledge_base.title'),
                  onClick: navigateBack,
                },
                ...breadcrumbs,
              ]}
            />
          )}
          <CustomForm
            key="knowledge-base-article-form"
            onSubmit={saveArticle}
            loading={isCreatingArticle || isEditingArticle}
            backButton
            buttonsOffset={6}
            fields={[
              {
                type: 'text',
                name: 'title',
                label: t('title'),
                initalValue: article?.title,
                rules: [
                  {
                    required: true,
                    message: t('form_errors.title'),
                  },
                ],
              },
              {
                type: 'tree_select',
                name: 'category_id',
                label: t('knowledge_base.categories.singular'),
                initalValue: state?.categoryId ?? article?.category_id,
                selectOptions: categoryTreeData,
                rules: [],
              },
              {
                type: 'texteditor',
                name: 'content',
                label: t('knowledge_base.articles.form.fields.content'),
                additionalProps: {
                  image: true,
                },
                initalValue: articleText,
                rules: [
                  {
                    required: true,
                    message: t('form_errors.text'),
                  },
                ],
              },
            ]}
          />
        </Col>
      </Row>
    );
  };

  return <KnowledgeBaseLayout>{renderContent()}</KnowledgeBaseLayout>;
}
